import '../../../i18n';
import {useTranslation} from 'react-i18next';
import React from 'react';
import styled from 'styled-components';
import { IoMdPricetags } from 'react-icons/io';
import { AuthContext } from '../../../util/context';
import { PALE_WHITE_1, PALE_WHITE_6, PALE_WHITE_8 } from '../../../appearance/Colors';

function AIInsightsPlugin({ message, frameless = false }) {
    const {t: lnT} = useTranslation();
    const searchContext = React.useContext(AuthContext)?.context;
    const canSearchTags = typeof searchContext?.setSearchTerm === 'function' &&
        typeof searchContext?.setSearchActive === 'function';
    const searchKeyword = (keyword, event) => {
        event.stopPropagation();
        const tag = `#${keyword.trim().replace(/^#+/, '')}`;
        searchContext.setSearchTerm(tag);
        searchContext.setSearchActive(true);
    };

    const caption = message?.caption?.trim();
    const keywords = Array.isArray(message?.keywords)
        ? message.keywords.filter(keyword => typeof keyword === 'string' && keyword.trim())
        : [];

    if (!caption && keywords.length === 0 && !message?.structuredContent && !message?.ocrText && !message?.documentText) {
        return null;
    }

    return (
        <Wrapper>
            {caption ? (
                <CaptionCard $frameless={frameless}>
                    <span style={{ color: PALE_WHITE_8, fontWeight: 600 }}>{lnT('messages:caption')}</span> {caption}
                </CaptionCard>
            ) : null}
            {keywords.length > 0 ? (
                <KeywordsRow>
                    <IoMdPricetags size={14} color={PALE_WHITE_8} />
                    {keywords.map((keyword, index) => (
                        <KeywordTag
                            as={canSearchTags ? 'button' : 'span'}
                            type={canSearchTags ? 'button' : undefined}
                            onClick={canSearchTags ? event => searchKeyword(keyword, event) : undefined}
                            $clickable={canSearchTags}
                            $frameless={frameless}
                            key={`${message?.id || 'message'}-plugin-keyword-${index}`}
                        >
                            #{keyword.trim().replace(/^#+/, '')}
                        </KeywordTag>
                    ))}
                </KeywordsRow>
            ) : null}
            {message?.structuredContent ? (
                <CaptionCard $frameless={frameless}>
                    <strong>{message.structuredContent.title}</strong>
                    <div>{message.structuredContent.summary}</div>
                    {(message.structuredContent.metadata || []).map(item => (
                        <div key={item.key}>{[item.label || item.key, item.value].filter(Boolean).join(': ')}</div>
                    ))}
                    {(message.structuredContent.sections || []).map(section => (
                        <div key={section.id}>
                            <strong>{section.title}</strong>
                            {(section.entries || []).map(entry => (
                                <div key={entry.id}>{[
                                    [entry.quantity, entry.unit, entry.label].filter(Boolean).join(' '),
                                    entry.text, entry.detail, entry.duration
                                ].filter(Boolean).join(' — ')}</div>
                            ))}
                        </div>
                    ))}
                </CaptionCard>
            ) : null}
            {message?.ocrText || message?.documentText ? (
                <CaptionCard $frameless={frameless} style={{ whiteSpace: 'pre-wrap' }}>
                    <strong>{lnT('common:textDocument')}</strong>
                    <div>{[message.ocrText, message.documentText].filter(Boolean).join('\n\n')}</div>
                </CaptionCard>
            ) : null}
        </Wrapper>
    );
}

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const CaptionCard = styled.div`
    padding: ${props => props.$frameless ? '0' : '8px 10px'};
    border-radius: 12px;
    background-color: ${props => props.$frameless ? 'transparent' : 'rgba(255, 255, 255, 0.03)'};
    border: ${props => props.$frameless ? 'none' : `1px solid ${PALE_WHITE_1}`};
    color: ${PALE_WHITE_6};
    font-size: 13px;
    line-height: 1.4;
`;

const KeywordsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
`;

const KeywordTag = styled.span`
    font-family: inherit;
    cursor: ${props => props.$clickable ? 'pointer' : 'default'};
    transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
    &:hover {
        color: ${props => props.$clickable ? PALE_WHITE_8 : PALE_WHITE_6};
        background-color: ${props => props.$clickable ? 'rgba(255, 255, 255, 0.12)' : 'transparent'};
        border-color: ${props => props.$clickable ? 'rgba(255, 255, 255, 0.25)' : 'transparent'};
    }
    &:focus-visible { outline: 2px solid ${PALE_WHITE_6}; outline-offset: 3px; }
    font-size: 11px;
    padding: ${props => props.$frameless && !props.$clickable ? '0' : '4px 9px'};
    border-radius: 999px;
    border: ${props => props.$frameless && !props.$clickable ? 'none' : `1px solid ${PALE_WHITE_1}`};
    color: ${PALE_WHITE_6};
    background-color: ${props => props.$clickable ? 'rgba(255, 255, 255, 0.06)' : props.$frameless ? 'transparent' : 'rgba(255, 255, 255, 0.03)'};
`;

export default AIInsightsPlugin;
